// "Invite teammate": port of InviteMemberSheet (GroundTeam/Views/Sync/InviteMemberSheet.swift). Pick a profile without
// an account or enter a new person, an optional email and a role; Create invitation saves the profile, waits until the
// server has it, calls `invite_member` and turns into the share view. The rules and the orchestration are in
// invite.ts; this file is the form.

import { Check, Ticket } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import { useSession } from '@/auth/AuthProvider';
import { USER_ROLE_TITLES } from '@/contracts/enums';
import type { Role, TeamInvitation } from '@/contracts/wire';
import { useMe, useTeams, useUsers } from '@/data/hooks';
import { fullName } from '@/domain/team';
import { Avatar, Button, Dialog, Field, GT_PALETTE, Input, SearchField, SegmentedControl, Select, cn } from '@/ui';
import {
  INVITE_ROLES,
  canSendInvite,
  inviteCandidates,
  inviteEmailProblem,
  inviteErrorMessage,
  prepareInviteProfile,
  sendInvitation,
  type InviteSource,
} from './invite';
import { InviteShareView, useCompanyName } from './InviteShareView';
import { ACCOUNT_STATUS_TITLES, type AccountInfo } from './members';
import { RoleChip } from './parts';
import { useInviteDeps } from './teamApi';

export interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
  /** Profiles that already have an account (left out of the picker). */
  memberProfileIds: ReadonlySet<string>;
  /** Account status by lower-case profile id, to mark people who already have an open invitation. */
  accountOf?: (profileId: string) => AccountInfo | null;
  /** Opens with this profile picked (the row action "Invite…"). */
  initialProfileId?: string | null;
  /** After the server created the invitation (refresh the roster). */
  onInvited?: (invitation: TeamInvitation) => void;
}

export function InviteDialog(props: InviteDialogProps) {
  // Mounted only while open, so every opening starts from a clean form.
  if (!props.open) return null;
  return <OpenInviteDialog {...props} />;
}

function OpenInviteDialog({ onClose, memberProfileIds, accountOf, initialProfileId, onInvited }: InviteDialogProps) {
  const session = useSession();
  const users = useUsers();
  const teams = useTeams();
  const me = useMe()?.value ?? null;
  const companyName = useCompanyName();
  const deps = useInviteDeps();
  // The submit button sits in the dialog footer, outside the form; `form=` ties it (and Enter in a field) to it.
  const formId = useId();

  const [createdProfileId, setCreatedProfileId] = useState<string | null>(null);
  const candidates = useMemo(
    () => inviteCandidates(users.values, memberProfileIds, session.profileId, createdProfileId),
    [users.values, memberProfileIds, session.profileId, createdProfileId],
  );
  const initial = initialProfileId ? candidates.find((u) => u.id.toLowerCase() === initialProfileId.toLowerCase()) : undefined;

  const [source, setSource] = useState<InviteSource>(() => (candidates.length === 0 && !initial ? 'new' : 'existing'));
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(initial?.id ?? null);
  const [email, setEmail] = useState(initial?.email ?? '');
  const [role, setRole] = useState<Role>(initial?.role ?? 'rep');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [filter, setFilter] = useState('');
  const [sending, setSending] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [sent, setSent] = useState<TeamInvitation | null>(null);
  // `sending` reaches the button one render late: two Enter presses in a row would otherwise both pass `canSend` and
  // create two profiles (and two invitations) for a new person.
  const inFlight = useRef(false);

  const excludingId = source === 'existing' ? selectedProfileId : createdProfileId;
  const emailProblem = inviteEmailProblem(email, users.values, excludingId);
  const canSend = canSendInvite({ source, selectedProfileId, firstName, email, sending }, emailProblem);

  const select = (id: string) => {
    const user = candidates.find((u) => u.id === id);
    if (!user) return;
    setSelectedProfileId(user.id);
    setEmail(user.email);
    setRole(user.role);
    setErrorText(null);
  };

  // Never build an edit from an item a newer app wrote (its best-effort decode may lack keys).
  const editableUser = (profileId: string | null) => {
    const item = users.get(profileId);
    return item && !item.undecodable ? item.value : null;
  };

  const send = async () => {
    if (!canSend || inFlight.current) return;
    const address = email.trim() === '' ? null : email.trim();
    const prepared = prepareInviteProfile({
      source,
      selected: source === 'existing' ? editableUser(selectedProfileId) : null,
      created: editableUser(createdProfileId),
      firstName,
      lastName,
      email: address,
      role,
      me,
      teams: teams.values,
      colorHex: GT_PALETTE[Math.floor(Math.random() * GT_PALETTE.length)]?.hex ?? '#2673E6',
    });
    if (!prepared) {
      setErrorText('Pick a profile first.');
      return;
    }
    inFlight.current = true;
    setSending(true);
    setErrorText(null);
    try {
      // Kept before the save: a retry after a failed invite patches this profile instead of making another.
      if (prepared.created) setCreatedProfileId(prepared.profileId);
      const invitation = await sendInvitation(prepared, { companyId: session.companyId, email: address, role }, deps);
      setSent(invitation);
      onInvited?.(invitation);
    } catch (error) {
      setErrorText(inviteErrorMessage(error, companyName));
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  const sentName = sent ? fullName(users.get(sent.profile_id)?.value) : '';

  return (
    <Dialog
      open
      onClose={onClose}
      // Closing mid-send would leave an invitation whose code was never shown.
      dismissible={!sending}
      title={sent ? 'Invitation ready' : 'Invite teammate'}
      description={
        sent ? undefined : 'Each person creates their own account and confirms the invitation before joining.'
      }
      size="md"
      footer={
        sent ? (
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button type="submit" form={formId} variant="primary" icon={<Ticket />} loading={sending} disabled={!canSend}>
              {sending ? 'Creating…' : 'Create invitation'}
            </Button>
          </>
        )
      }
    >
      {sent ? (
        <InviteShareView invitation={sent} name={sentName} />
      ) : (
        <form
          id={formId}
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <fieldset disabled={sending} className="flex flex-col gap-2">
            <SegmentedControl<InviteSource>
              label="Who"
              value={source}
              onChange={(v) => {
                setSource(v);
                setErrorText(null);
              }}
              options={[
                { value: 'existing', label: 'Existing profile' },
                { value: 'new', label: 'New person' },
              ]}
              fullWidth
            />
            <p className="text-xs text-fg-muted">
              {source === 'existing'
                ? 'Pick the Team profile this person will use. Their stores, routes and history stay linked to it.'
                : 'Creates a new profile in Team, then invites them.'}
            </p>
          </fieldset>

          {source === 'existing' ? (
            <ProfilePicker
              candidates={candidates}
              selectedId={selectedProfileId}
              onSelect={select}
              filter={filter}
              onFilterChange={setFilter}
              accountOf={accountOf}
              disabled={sending}
            />
          ) : (
            <fieldset disabled={sending} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <legend className="mb-2 text-[11px] font-semibold tracking-[0.05em] text-fg-muted uppercase">Name</legend>
              <Field label="First name" required>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="off" data-autofocus />
              </Field>
              <Field label="Last name">
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="off" />
              </Field>
            </fieldset>
          )}

          <fieldset disabled={sending} className="flex flex-col gap-3">
            <legend className="mb-2 text-[11px] font-semibold tracking-[0.05em] text-fg-muted uppercase">Invitation</legend>
            <Field
              label="Email (optional)"
              error={emailProblem ?? undefined}
              hint={
                emailProblem
                  ? undefined
                  : 'If they sign up with this email, the invitation appears automatically. Either way you get an invite code to share, and they confirm before joining.'
              }
            >
              <Input
                type="email"
                inputMode="email"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field label="Role" hint="The role is enforced by the server.">
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                options={INVITE_ROLES.map((r) => ({ value: r, label: USER_ROLE_TITLES[r] }))}
              />
            </Field>
          </fieldset>

          {errorText && (
            <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger-text">
              {errorText}
            </p>
          )}
        </form>
      )}
    </Dialog>
  );
}

function ProfilePicker({
  candidates,
  selectedId,
  onSelect,
  filter,
  onFilterChange,
  accountOf,
  disabled,
}: {
  candidates: readonly { id: string; firstName: string; lastName: string; email: string; role: Role; colorHex: string }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  filter: string;
  onFilterChange: (value: string) => void;
  accountOf?: (profileId: string) => AccountInfo | null;
  disabled: boolean;
}) {
  const groupName = useId();
  const q = filter.trim().toLowerCase();
  const shown = q === '' ? candidates : candidates.filter((u) => `${fullName(u)} ${u.email}`.toLowerCase().includes(q));

  if (candidates.length === 0) {
    return (
      <p className="rounded-card border border-line bg-surface-2 px-4 py-3 text-sm text-fg-muted">
        Everyone in Team already has access or an account. Switch to New person.
      </p>
    );
  }
  return (
    <fieldset disabled={disabled} className="flex min-w-0 flex-col gap-2">
      <legend className="mb-2 text-[11px] font-semibold tracking-[0.05em] text-fg-muted uppercase">Profile</legend>
      {candidates.length > 6 && (
        <SearchField value={filter} onValueChange={onFilterChange} label="Find a profile" size="sm" />
      )}
      <div className="max-h-64 overflow-y-auto rounded-card border border-line bg-surface gt-scroll">
        {shown.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-fg-muted">No profiles match “{filter.trim()}”.</p>
        ) : (
          <ul className="divide-y divide-line">
            {shown.map((u) => {
              const name = fullName(u);
              const selected = u.id === selectedId;
              const account = accountOf?.(u.id) ?? null;
              const pending = account && (account.status === 'invited' || account.status === 'expired');
              return (
                <li key={u.id}>
                  <label
                    className={cn(
                      'flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-surface-2',
                      'has-[:focus-visible]:shadow-[inset_0_0_0_2px_var(--gt-focus)]',
                      selected && 'bg-primary-soft hover:bg-primary-soft',
                    )}
                  >
                    <input
                      type="radio"
                      name={groupName}
                      value={u.id}
                      checked={selected}
                      onChange={() => onSelect(u.id)}
                      className="sr-only"
                    />
                    <Avatar name={name || 'Unnamed'} colorHex={u.colorHex} size={34} label={null} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-fg">{name || 'Unnamed'}</span>
                      <span className="block truncate text-xs text-fg-muted">
                        {u.email || 'No email (invite code only)'}
                        {pending ? ` · ${ACCOUNT_STATUS_TITLES[account.status]}` : ''}
                      </span>
                    </span>
                    <RoleChip role={u.role} size="xs" />
                    <span
                      aria-hidden
                      className={cn(
                        'flex size-5 shrink-0 items-center justify-center rounded-full border',
                        selected ? 'gt-primary-fill border-transparent text-white' : 'border-line-strong',
                      )}
                    >
                      {selected && <Check className="size-3" strokeWidth={3} />}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </fieldset>
  );
}
